import { connect } from 'react-redux';
import { createSelector, defaultMemoize } from 'reselect';
import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  MESSAGE_REPLIED,
  MESSAGE_SENT,
  MESSAGE_FILE_UPLOADED,
  MESSAGE_CODE_FORMAT_USED,
} from '@core/constants/analytics';
import { MESSAGE_CONTENT_TYPE } from '@app/constants/texts';
import {
  operationsResponsive as creators,
  selectorsResponsive as inboxSelectors,
} from '../../../../../../store/conversations';
import * as socketCreators from '../../../../../../../core/actions/socket';
import * as userSelectors from '../../../../../../../core/selectors/user';
import * as usersSelectors from '../../../../../../../core/selectors/users';
import WriteZone from './WriteZone';

const noop = () => null;
// Constants
const REPLY = 'reply';
const DIRECT = 'direct';

const currentConversationId = defaultMemoize(
  inboxSelectors.currentConversationId
);
const messages = defaultMemoize(inboxSelectors.messages);
const currentConversationMessageIds = defaultMemoize((state) => {
  const conversationId = currentConversationId(state);
  return inboxSelectors.conversationMessages(state, conversationId);
});
const conversationContent = defaultMemoize((state) => {
  const conversationId = currentConversationId(state);
  return inboxSelectors.conversationDraft(state, conversationId);
});

const replyingMessage = defaultMemoize((state) =>
  inboxSelectors.replyingToMessage(state)
);

const latestMessageFrom = createSelector(
  messages,
  currentConversationMessageIds,
  userSelectors.currentUserId,
  (messageStore = {}, conversationMessages = [], currentUserId) => {
    let message;
    for (let i = conversationMessages.length - 1; i >= 0; i -= 1) {
      message = messageStore[conversationMessages[i]];
      if (message && message.writer === currentUserId) {
        return message.id;
      }
    }

    return null;
  }
);

const recipientSelector = defaultMemoize((state) => {
  const conversationId = currentConversationId(state);
  const recipientId = inboxSelectors.recipientId(state, conversationId);
  return usersSelectors.user(state, recipientId);
});

const mapStateToProps = createSelector(
  currentConversationId,
  latestMessageFrom,
  recipientSelector,
  conversationContent,
  replyingMessage,
  (conversationId, latestOwnMessageId, recipient, content, reply) => ({
    conversationId,
    latestOwnMessageId,
    placeholder: recipient
      ? `Message ${recipient.firstName}...`
      : 'Write a message...',
    reply,
    content,
    // Adding a new prop messageType to differentiate between reply and direct message for analytics tracking
    messageType: reply ? REPLY : DIRECT,
  })
);

const trackMessageAnalytics = (content, messageType) => {
  const hasFile = content?.value?.some(
    (block) => block?.type === MESSAGE_CONTENT_TYPE.FILE
  );
  const hasCode = content?.value?.some(
    (block) =>
      block?.type === MESSAGE_CONTENT_TYPE.CODE ||
      block?.type === MESSAGE_CONTENT_TYPE.LATEX
  );
  if (hasFile) {
    analyticsTrackEvent(MESSAGE_FILE_UPLOADED);
  }
  if (hasCode) {
    analyticsTrackEvent(MESSAGE_CODE_FORMAT_USED);
  }
  // Added analytics tracking for message sent and message replied events
  if (messageType === REPLY) {
    analyticsTrackEvent(MESSAGE_REPLIED);
  }
  analyticsTrackEvent(MESSAGE_SENT);
};

const mapDispatchToProps = (dispatch) => ({
  onEdit: (id) => dispatch(creators.openEditMessage(id)),
  onReturn: async (content, conversationId, messageType) => {
    try {
      await dispatch(creators.sendMessage(content, conversationId));
      // Track events for analytics
      trackMessageAnalytics(content, messageType);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error sending message:', err);
    }
  },
  sendTyping: (id) => dispatch(socketCreators.userIsTyping(id)),
  updateContent: (conversationId, messageData) =>
    dispatch(creators.updateMessageDraft(conversationId, messageData)),
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { latestOwnMessageId, ...otherStateProps } = stateProps;
  const { onEdit, sendTyping, ...otherDispatchProps } = dispatchProps;

  return {
    ...otherStateProps,
    ...otherDispatchProps,
    ...ownProps,
    onEdit: latestOwnMessageId ? () => onEdit(latestOwnMessageId) : noop,
    sendTyping: () => sendTyping(otherStateProps.conversationId),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps, {
  forwardRef: true,
})(WriteZone);
